import { useCallback } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  expandShapeSnapshotsForClipboard,
  remapClipboardParentGroupId,
  shapeSnapshotNestingDepth,
} from '@/lib/groups/clipboard-group-policy'
import { isGroupShape } from '@/lib/groups/group-kind'
import { isShapeLockedForUserEdit } from '@/lib/groups/element-edit-policy'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { ShapeCollectionApi } from './use-shape-collection'
import type { SelectionPresentationApi } from './use-selection-presentation'
import type { ShapeSelectionApi } from './use-shape-selection'

export function useAltDragDuplicate(
  ctx: ShapeManagerStateApi & ShapeCollectionApi & SelectionPresentationApi & ShapeSelectionApi
) {
  const {
    addShapeData,
    generateShapeId,
    selectShapes,
    selectedShapeIdsRef,
    shapesById,
    shapesRef,
  } = ctx

  /**
   * Alt+drag on a shape body duplicates the current selection in place (same position, same
   * relative group nesting) and re-selects the copies, so the caller's subsequent move-gesture
   * setup drags the NEW shapes instead of the originals — which stay untouched at their
   * original position. Mirrors the group-expansion/depth-ordering `pasteFromClipboard`
   * (center-canvas/hooks/use-clipboard-paste.ts) already uses for Ctrl+V, just without the internal-clipboard
   * round-trip: the copies need to exist and be selected synchronously, within the same
   * pointer-down tick `startInteraction` reads the selection for the drag it's about to start
   * (the `selectedShapes` memo below reflects the PREVIOUS render, so it would still resolve to
   * the originals within this same call otherwise).
   *
   * Returns `undefined` (caller falls back to a normal, non-duplicating move) when nothing is
   * selected or the selection includes a locked object — self or via a locked ancestor group —
   * matching Copy's own refusal to duplicate a locked object.
   */
  const duplicateSelectionForAltDrag = useCallback(():
    { shapes: ShapeData[]; sourceIdToNewId: ReadonlyMap<string, string> } | undefined => {
    const selectedIds = selectedShapeIdsRef.current

    if (selectedIds.length === 0) return undefined

    const toDuplicate = expandShapeSnapshotsForClipboard(selectedIds, shapesRef.current)

    if (toDuplicate.length === 0) return undefined
    if (toDuplicate.some((shape) => isShapeLockedForUserEdit(shape, shapesById))) return undefined

    const idMap = new Map<string, string>()

    for (const shape of toDuplicate) {
      idMap.set(shape.id, generateShapeId())
    }

    const byId = new Map(toDuplicate.map((shape) => [shape.id, shape]))
    const ordered = [...toDuplicate].sort(
      (a, b) => shapeSnapshotNestingDepth(b, byId) - shapeSnapshotNestingDepth(a, byId)
    )
    const historyTxnId = `duplicate:${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
    const createdById = new Map<string, ShapeData>()

    for (const snapshot of ordered) {
      const clone: ShapeData = {
        ...snapshot,
        id: idMap.get(snapshot.id)!,
        locked: false,
        frozen: false,
        // A duplicate is a new, independent object — same rule paste already follows
        // (WMPCM-6852 uid-collision corruption): it must not inherit the original's
        // text-linking chain identity.
        uid: undefined,
        parentGroupId: remapClipboardParentGroupId(snapshot.parentGroupId, idMap),
      }

      const created = addShapeData(clone, {
        selectAfterCreate: false,
        meta: { historyTxnId, ...(isGroupShape(clone) ? { isGroupCreation: true } : {}) },
      })

      createdById.set(created.id, created)
    }

    const topLevelNewIds = selectedIds
      .map((id) => idMap.get(id))
      .filter((id): id is string => id != null)

    selectShapes(topLevelNewIds)

    const topLevelShapes = topLevelNewIds
      .map((id) => createdById.get(id))
      .filter((shape): shape is ShapeData => shape != null)

    return topLevelShapes.length > 0
      ? { shapes: topLevelShapes, sourceIdToNewId: idMap }
      : undefined
  }, [addShapeData, generateShapeId, selectShapes, shapesById, selectedShapeIdsRef, shapesRef])

  return { duplicateSelectionForAltDrag }
}

export type AltDragDuplicateApi = ReturnType<typeof useAltDragDuplicate>
